import React, { useState, useEffect } from 'react';
import { compose } from 'redux';
import { connect, useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors.js';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers.js';
import { requireListingImage } from '../../util/configHelpers';
import { logFunnelEvent } from '../../util/analytics';
import { formatEventType, getCategoryEmoji, allInMoney } from '../../util/listingHelpers';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import {
  OFFER,
  REQUEST,
  isBookingProcess,
  isNegotiationProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

// Global ducks (for Redux actions and thunks)
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';

// Shared components
import {
  H4,
  H3,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
  MobileCollapsible,
  SectionText,
  TierSelector,
} from '../../components';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  requestQuote,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
} from './ListingPage.duck';

import { toggleFavorite, selectIsFavorited } from '../../ducks/favorites.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmitQuoteRequest,
  handleNavigateToMakeOfferPage,
  handleNavigateToRequestQuotePage,
  handleSubmit,
  priceForSchemaMaybe,
} from './ListingPage.shared';
import ActionBarMaybe from './ActionBarMaybe';
import SectionReviews from './SectionReviews';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import SectionAvailableAddOns from './SectionAvailableAddOns/SectionAvailableAddOns';
import SectionHostHeader from './SectionHostHeader/SectionHostHeader';
import SectionLogisticsAndPolicies from './SectionLogisticsAndPolicies/SectionLogisticsAndPolicies';
import SectionPackageContents from './SectionPackageContents/SectionPackageContents';
import SectionFulfillmentOptions from './SectionFulfillmentOptions/SectionFulfillmentOptions';
import SectionSimilarListings from './SectionSimilarListings/SectionSimilarListings';
import SectionMapMaybe from './SectionMapMaybe';
import SectionGallery from './SectionGallery';
import CustomListingFields from './CustomListingFields';
import MobileBookingBar from './MobileBookingBar/MobileBookingBar';
import MobileQuoteSheet from './MobileQuoteSheet/MobileQuoteSheet';
import QuoteRequestPanel from './QuoteRequestPanel/QuoteRequestPanel';
import { resolvePricingMode } from '../../util/editListingHelpers';

import css from './ListingPage.module.css';
import { cityStateLabel } from '../../util/search';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID } = sdkTypes;

// Phase 2: crisp SVG icons for the Share / Save actions. Replaces the
// Unicode glyphs (⤴ / ♡), which rendered inconsistently across platforms.
// currentColor inherits the link's text color; the heart fills when saved.
const ShareIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const HeartIcon = ({ filled }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    // IMPORTANT: a GLOBAL `svg { fill: currentColor }` rule overrides the
    // `fill="none"` ATTRIBUTE above (CSS rule beats a presentation attr),
    // so we MUST set fill in the inline `style` — which outranks it — for
    // both states. Otherwise the unsaved heart fills with currentColor
    // (near-black) and looks like a solid black heart instead of a hollow
    // outline. Saved = coral fill; unsaved = no fill + soft grey stroke.
    style={
      filled
        ? {
            fill: 'var(--rentTheEventPrimary, #E8663C)',
            stroke: 'var(--rentTheEventPrimary, #E8663C)',
          }
        : { fill: 'none', stroke: 'var(--colorGrey500, #6B5E52)' }
    }
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 1 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

export const ListingPageComponent = props => {
  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews = [],
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    requestQuoteInProgress,
    requestQuoteError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onRequestQuote,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    showOwnListingsOnly,
    ...restOfProps
  } = props;

  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === rawParams.id
  );
  const [mounted, setMounted] = useState(false);
  const [selectedTierKey, setSelectedTierKey] = useState(null);
  const [selectedFulfillment, setSelectedFulfillment] = useState(null);
  const [selectedDeliveryAddress, setSelectedDeliveryAddress] = useState(null);
  // Stream 3 PR-3: mobile quote-request bottom sheet. The desktop
  // QuoteRequestPanel renders inline in the right rail; on mobile it
  // lives in this bottom-sheet modal, opened from the MobileBookingBar.
  const [quoteSheetOpen, setQuoteSheetOpen] = useState(false);
  // Transient "Link copied" pill after the clipboard share fallback.
  const [shareFeedback, setShareFeedback] = useState(null);
  useEffect(() => {
    if (!shareFeedback) return undefined;
    const timer = setTimeout(() => setShareFeedback(null), 2000);
    return () => clearTimeout(timer);
  }, [shareFeedback]);

  // Favorites
  const reduxDispatch = useDispatch();
  // Optimistic favorite: flip the heart immediately, then reconcile with
  // the server. Previously the heart only changed after the profile
  // round-trip AND the thunk swallowed any error — so a click looked like
  // it did nothing. Now we give instant feedback and surface failures.
  const serverFavorited = useSelector(state => selectIsFavorited(state, rawParams.id));
  const [optimisticFavorite, setOptimisticFavorite] = useState(null);
  const isFavorited = optimisticFavorite ?? serverFavorited;
  useEffect(() => {
    // Clear the optimistic flag once Redux reflects the saved state.
    setOptimisticFavorite(null);
  }, [serverFavorited]);
  const handleToggleFavorite = () => {
    if (!isAuthenticated) return;
    setOptimisticFavorite(!isFavorited);
    reduxDispatch(toggleFavorite(rawParams.id))
      .unwrap()
      .catch(() => {
        setOptimisticFavorite(null); // revert the optimistic flip
        window.alert('Could not update your saved kits. Please try again.');
      });
  };

  useEffect(() => {
    setMounted(true);

    // Track listing viewed event for analytics
    if (rawParams.id) {
      logFunnelEvent('listing_viewed', rawParams.id);
    }
  }, [rawParams.id]);

  // RTE: Auto-select first tier — read listing from Redux inside effect to avoid reference issues
  useEffect(() => {
    const listing = getListing(new UUID(rawParams.id)) || getOwnListing(new UUID(rawParams.id));
    const tiers = listing?.attributes?.publicData?.tiers;
    const tierKeys = tiers ? Object.keys(tiers) : [];
    if (tierKeys.length > 0 && !selectedTierKey) {
      setSelectedTierKey(tierKeys[0]);
    }
  }, [rawParams.id, selectedTierKey]); // eslint-disable-line

  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isVariant = rawParams.variant != null;
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const tiers = publicData.tiers || null;
  const tierKeys = tiers ? Object.keys(tiers) : [];
  const isRTEListing = !!tiers && tierKeys.length > 0;

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const { listingType, transactionProcessAlias, unitType } = publicData;
  if (!(listingType && transactionProcessAlias && unitType)) {
    // Listing should always contain listingType, transactionProcessAlias and unitType)
    return (
      <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
    );
  }
  const validListingTypes = listingConfig.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);
  const showDescription = foundListingTypeConfig?.defaultListingFields?.description;

  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const isNegotiation = isNegotiationProcess(processName);
  const processType = isBooking
    ? 'booking'
    : isPurchase
    ? 'purchase'
    : isNegotiation
    ? 'negotiation'
    : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const authorNeedsPayoutDetails =
    ['booking', 'purchase'].includes(processType) || (isNegotiation && unitType === OFFER);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && (authorNeedsPayoutDetails && !currentUser?.attributes?.stripeConnected);
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);

  // Phase 2: numeric review rating for the metadata row and booking
  // card. Zero-review state is graceful — no star is rendered and the
  // meta row falls back to "New listing".
  const reviewCount = reviews.length;
  const averageRating =
    reviewCount > 0
      ? reviews.reduce((sum, r) => sum + r.attributes.rating, 0) / reviewCount
      : null;

  // At-a-glance stat line — renter-facing specs (capacity + logistics).
  const fulfillment = Array.isArray(publicData.fulfillment) ? publicData.fulfillment : [];
  const hasPickup = fulfillment.includes('pickup');
  const hasDelivery = fulfillment.includes('delivery');
  const fulfillmentLabel =
    hasPickup && hasDelivery
      ? 'Pickup or delivery'
      : hasDelivery
      ? 'Delivery available'
      : hasPickup
      ? 'Pickup only'
      : null;
  const statParts = [
    publicData.maxGuestCount ? `Up to ${publicData.maxGuestCount} guests` : null,
    fulfillmentLabel,
    tierKeys.length > 1 ? `${tierKeys.length} options` : null,
  ].filter(Boolean);

  // Share via the Web Share API, falling back to copy-to-clipboard.
  const handleShare = () => {
    if (typeof window === 'undefined') return;
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title, url }).catch(e => {
        // Closing the share sheet rejects with AbortError — that's normal.
        if (!e || e.name !== 'AbortError') {
          window.alert('Could not open the share menu. You can copy the link from the address bar.');
        }
      });
    } else if (navigator.clipboard) {
      navigator.clipboard
        .writeText(url)
        .then(() => setShareFeedback('Link copied'))
        .catch(() => {
          window.alert('Could not copy the link. You can copy it from the address bar.');
        });
    }
  };

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  // Note: this is for inquire transition to inquiry state in booking, purchase and negotiation processes.
  // Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });
  // Custom-quote "request to book": fires transition/request-quote (renter
  // = customer) instead of the inquiry transition that inverted roles.
  const onSubmitQuoteRequest = handleSubmitQuoteRequest({
    ...commonParams,
    currentUser,
    location,
    getListing,
    onRequestQuote,
  });
  // This is to navigate to MakeOfferPage when InvokeNegotiationForm is submitted
  const onNavigateToMakeOfferPage = handleNavigateToMakeOfferPage({
    ...commonParams,
    getListing,
  });
  // This is to navigate to MakeOfferPage when InvokeNegotiationForm is submitted
  const onNavigateToRequestQuotePage = handleNavigateToRequestQuotePage({
    ...commonParams,
    getListing,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
  });

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else if (isNegotiation && unitType === REQUEST) {
      onNavigateToMakeOfferPage(values);
    } else if (isNegotiation && unitType === OFFER) {
      onNavigateToRequestQuotePage(values);
    } else {
      onSubmit(values);
    }
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  // City-bearing title for local SEO ("… — Party Kit Rental in Santa
  // Barbara, CA | Merrily"). publicData.location.address holds only
  // city/state/zip since the address-privacy fix (#336), and
  // cityStateLabel is the same fail-closed parser the cards use — no
  // city recognized ⇒ fall back to the plain title.
  const cityLabel = cityStateLabel(currentListing.attributes?.publicData?.location?.address);
  // Owner correction 2026-07-03: the metro's towns are minutes apart, so a
  // kit outside Santa Barbara proper must still be findable by "party kit
  // rental santa barbara" searches — anchor every non-SB city to the metro
  // ("… in Goleta · Santa Barbara Area"). SB-proper kits keep the plain
  // city form (repeating "Santa Barbara" twice would read as keyword spam).
  const isSantaBarbaraProper = cityLabel && cityLabel.startsWith('Santa Barbara');
  const schemaTitle = cityLabel
    ? intl.formatMessage(
        {
          id: isSantaBarbaraProper
            ? 'ListingPage.schemaTitleWithCity'
            : 'ListingPage.schemaTitleWithCityInMetro',
        },
        // "Goleta, CA" → "Goleta" in the metro form; the metro suffix
        // already places it, and the shorter title truncates less in SERPs.
        { title, city: isSantaBarbaraProper ? cityLabel : cityLabel.split(',')[0], marketplaceName }
      )
    : intl.formatMessage(
        { id: 'ListingPage.schemaTitle' },
        { title, price: formattedPrice, marketplaceName }
      );
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability = !currentListing.currentStock
    ? null
    : currentStock > 0
    ? 'https://schema.org/InStock'
    : 'https://schema.org/OutOfStock';

  const availabilityMaybe = schemaAvailability ? { availability: schemaAvailability } : {};
  const noIndexMaybe =
    currentListing.attributes.state === LISTING_STATE_CLOSED ? { noIndex: true } : {};

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      {...noIndexMaybe}
      schema={[
        {
          '@context': 'http://schema.org',
          '@type': 'Product',
          description: description,
          name: schemaTitle,
          image: schemaImages,
          offers: {
            '@type': 'Offer',
            url: productURL,
            // Emit the all-in price (base + 15% fee) the renter actually
            // pays — not the bare base price, which would leak our markup
            // into public structured data (crawlable / rich results).
            ...priceForSchemaMaybe(allInMoney(price)),
            ...availabilityMaybe,
          },
        },
        {
          '@context': 'http://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: config.marketplaceRootURL },
            { '@type': 'ListItem', position: 2, name: 'Browse kits', item: `${config.marketplaceRootURL}/s` },
            // Last crumb = the current page; Google doesn't need item here.
            { '@type': 'ListItem', position: 3, name: title },
          ],
        },
      ]}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        {/*
          Listing detail content. JSX source order matches DESKTOP order
          (gallery + body left, breadcrumb/title/booking right). Mobile
          rendering reorders the same elements via CSS `order:` on the
          .area_* classes -- see ListingPage.module.css and the design doc
          at docs/design/listing-detail-mobile-restructure.md.

          Why one tree instead of mobile-vs-desktop branches: keeps
          platforms in lockstep, avoids hydration mismatches, and lets the
          single CSS Grid handle both layouts. Adding/removing a section
          means: (1) wrap it in <div className={css.area_*}>,
          (2) assign a mobile `order:` value, (3) pick its desktop
          grid-column.
        */}
        <div className={css.contentWrapperForProductLayout}>
          {/* Action bars (full-width when present; own-listing only) */}
          <div className={css.area_actionBars}>
            {mounted && currentListing.id && noPayoutDetailsSetWithOwnListing ? (
              <ActionBarMaybe
                className={css.actionBarForProductLayout}
                isOwnListing={isOwnListing}
                listing={currentListing}
                showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
                currentUser={currentUser}
              />
            ) : null}
            {mounted && currentListing.id ? (
              <ActionBarMaybe
                className={css.actionBarForProductLayout}
                isOwnListing={isOwnListing}
                listing={currentListing}
                currentUser={currentUser}
                editParams={{
                  id: listingId.uuid,
                  slug: listingSlug,
                  type: listingPathParamType,
                  tab: listingTab,
                }}
              />
            ) : null}
          </div>

          {/*
            Stream 3 PR-2: layout restructured to match listing-detail-C.html:
            full-width breadcrumb → title row → photo gallery, then a two-
            column content grid below. Custom-quote (pricingMode === 'quote')
            listings render the right rail's QuoteRequestPanel instead of
            OrderPanel; the body's "Available add-ons" section appears in
            that branch only.

            `area_*` classes still carry mobile `order:` rules from the
            previous layout — mobile rendering is intentionally pre-Stream-3
            here. PR-3 reworks mobile (full-bleed carousel, sticky bottom
            bar, bottom-sheet modal). For now mobile shows the same content,
            just in single-column order.
          */}

          {/* Breadcrumb — full-width row above the title */}
          <div className={css.area_breadcrumb}>
            <nav className={css.detailBreadcrumb}>
              <a href="/" className={css.detailBreadcrumbLink}>Home</a>
              <span className={css.detailBreadcrumbSep}>/</span>
              {publicData.categoryLevel1 ? (
                <>
                  <a href={`/s?pub_categoryLevel1=${publicData.categoryLevel1}`} className={css.detailBreadcrumbLink}>
                    {formatEventType(publicData.categoryLevel1)}
                  </a>
                  <span className={css.detailBreadcrumbSep}>/</span>
                </>
              ) : null}
              <span className={css.detailBreadcrumbCurrent}>{title}</span>
            </nav>
          </div>

          {/* Title row — full-width: title · meta row, with kit type
              badge inline ("Book instantly" vs "Custom quote"). Keywords
              are internal search metadata — never rendered to renters. */}
          <div className={css.area_titleRow}>
            <div className={css.titleRowMain}>
              <h1 className={css.kitTitle}>{title}</h1>
              <div className={css.kitMetaRow}>
                {averageRating != null ? (
                  <span className={css.kitRating}>
                    <span className={css.kitRatingStar} aria-hidden="true">★</span>
                    {averageRating.toFixed(2)} ({reviewCount})
                  </span>
                ) : (
                  <span>New listing</span>
                )}
                {publicData.location?.address && (
                  <>
                    <span className={css.kitMetaDot} />
                    {/* cityStateLabel is the hardened post-privacy parser the cards
                        use; formatCityState assumed a street-first address and
                        rendered "California 93117, United States" once streets
                        were stripped from public data (owner report 2026-07-07). */}
                    <span>{cityStateLabel(publicData.location.address) || 'Santa Barbara area'}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Gallery — full-width above the two-column content grid */}
          {showListingImage && (
            <div className={css.area_gallery}>
              <div className={css.galleryRounded}>
                <SectionGallery
                  listing={currentListing}
                  variantPrefix={config.layout.listingImage.variantPrefix}
                  onManageDisableScrolling={onManageDisableScrolling}
                  heroActions={
                    <>
                      {shareFeedback && (
                        <span className={css.shareFeedback} role="status">
                          {shareFeedback}
                        </span>
                      )}
                      <button
                        type="button"
                        className={css.mosaicActionBtn}
                        onClick={handleShare}
                        aria-label="Share"
                      >
                        <ShareIcon />
                      </button>
                      {isAuthenticated && !isOwnListing && (
                        <button
                          type="button"
                          className={css.mosaicActionBtn}
                          onClick={handleToggleFavorite}
                          aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                        >
                          <HeartIcon filled={isFavorited} />
                        </button>
                      )}
                    </>
                  }
                />
              </div>
            </div>
          )}

          <div className={css.mainCol}>

          {/* Host header (compact, with verified credential badges) */}
          <div className={css.area_hostHeader}>
            <SectionHostHeader
              listing={currentListing}
              authorDisplayName={authorDisplayName}
            />
            {statParts.length > 0 && (
              <div className={css.statLine}>
                {statParts.map((part, i) => (
                  <React.Fragment key={part}>
                    {i > 0 && <span className={css.statDot} />}
                    <span>{part}</span>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>

          {/* About this kit — description only */}
          <div className={css.area_about}>
            <div className={css.aboutSection}>
              <h2 className={css.aboutHeading}>About this kit</h2>
              {showDescription && <p className={css.aboutText}>{description}</p>}
            </div>
          </div>

          {/* What's included */}
          <div className={css.area_pkgContents}>
            <SectionPackageContents
              listing={currentListing}
              selectedTierKey={selectedTierKey}
            />
          </div>

          {/* Available add-ons (custom-quote listings only) */}
          {resolvePricingMode(publicData.pricingMode) === 'quote' && (
            <div className={css.area_addons}>
              <SectionAvailableAddOns
                listing={currentListing}
                authorDisplayName={authorDisplayName}
              />
            </div>
          )}

          {/* Logistics & policies — read-only 4-cell grid */}
          <div className={css.area_logisticsAndPolicies}>
            <SectionLogisticsAndPolicies listing={currentListing} />
          </div>

          {/* Map / delivery range -- collapsed by default on mobile per
              the previous design (§5.2); passthrough on desktop. */}
          <div className={css.area_map}>
            <MobileCollapsible header="Show location & delivery range">
              <SectionMapMaybe
                geolocation={geolocation}
                publicData={publicData}
                listingId={currentListing.id}
                mapsConfig={config.maps}
              />
            </MobileCollapsible>
          </div>

          {/* Reviews */}
          <div className={css.area_reviews}>
            <SectionReviews reviews={reviews} fetchReviewsError={fetchReviewsError} />
          </div>

          {/* About the host (full author section; collapsed on mobile) */}
          <div className={css.area_host}>
            <MobileCollapsible
              header={authorDisplayName ? `About ${authorDisplayName}` : 'About the host'}
            >
              <SectionAuthorMaybe
                title={title}
                listing={currentListing}
                authorDisplayName={authorDisplayName}
                onContactUser={onContactUser}
                isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
                onCloseInquiryModal={() => setInquiryModalOpen(false)}
                sendInquiryError={sendInquiryError}
                sendInquiryInProgress={sendInquiryInProgress}
                onSubmitInquiry={onSubmitInquiry}
                currentUser={currentUser}
                onManageDisableScrolling={onManageDisableScrolling}
              />
            </MobileCollapsible>
          </div>

          {/* Similar listings (DESKTOP-ONLY) */}
          <div className={css.area_similar}>
            <SectionSimilarListings
              listing={currentListing}
              routeConfiguration={routeConfiguration}
              intl={intl}
            />
          </div>

          {/* Cancellation / how-it-works links */}
          <div className={css.area_links}>
            <a href="/p/faq#cancellation" className={css.policyLink}>
              Cancellation policy →
            </a>
            <a href="/p/about" className={css.policyLink}>
              How Merrily works →
            </a>
          </div>
          </div>{/* /mainCol */}

          <div className={css.orderCol}>
          {resolvePricingMode(publicData.pricingMode) === 'quote' ? (
            /* Custom-quote variant: starting price + quote-request form */
            <div className={css.area_dates}>
              <QuoteRequestPanel
                listing={currentListing}
                authorDisplayName={authorDisplayName}
                isOwnListing={isOwnListing}
                inProgress={requestQuoteInProgress}
                error={requestQuoteError}
                onSubmitQuoteRequest={onSubmitQuoteRequest}
              />
            </div>
          ) : (
            /* Standard kit variant: tier picker + fulfillment + dates +
                OrderPanel (existing infrastructure, retained verbatim) */
            <>
              {isRTEListing && tierKeys.length >= 1 && (
                <div className={css.area_tier}>
                  <TierSelector
                    tiers={tiers}
                    selectedTierKey={selectedTierKey}
                    onSelectTier={setSelectedTierKey}
                    currency={config.currency}
                  />
                </div>
              )}
              <div className={css.area_fulfillment}>
                <SectionFulfillmentOptions
                  className={css.bareFulfillment}
                  listing={currentListing}
                  selectedOption={selectedFulfillment}
                  onSelectOption={setSelectedFulfillment}
                  onDeliveryAddressChange={setSelectedDeliveryAddress}
                  addressFieldHiddenOnMobile
                />
              </div>
              <div className={css.area_dates}>
                <OrderPanel
                  className={classNames(css.productOrderPanel, {
                    [css.imagesEnabled]: showListingImage,
                  })}
                  listing={currentListing}
                  isOwnListing={isOwnListing}
                  onSubmit={handleOrderSubmit}
                  externalTierKey={selectedTierKey}
                  onExternalTierChange={setSelectedTierKey}
                  externalFulfillment={selectedFulfillment || 'pickup'}
                  onExternalFulfillmentChange={setSelectedFulfillment}
                  externalDeliveryAddress={selectedDeliveryAddress}
                  mobileExtrasContent={
                    <SectionFulfillmentOptions
                      listing={currentListing}
                      selectedOption={selectedFulfillment}
                      onSelectOption={setSelectedFulfillment}
                      onDeliveryAddressChange={setSelectedDeliveryAddress}
                    />
                  }
                  authorLink={
                    <NamedLink
                      className={css.authorNameLink}
                      name={isVariant ? 'ListingPageVariant' : 'ListingPage'}
                      params={params}
                      to={{ hash: '#author' }}
                    >
                      {authorDisplayName}
                    </NamedLink>
                  }
                  title={<FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />}
                  titleDesktop={
                    <H4 as="h1" className={css.orderPanelTitle}>
                      <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                    </H4>
                  }
                  payoutDetailsWarning={payoutDetailsWarning}
                  author={ensuredAuthor}
                  onManageDisableScrolling={onManageDisableScrolling}
                  onContactUser={onContactUser}
                  {...restOfProps}
                  validListingTypes={config.listing.listingTypes}
                  marketplaceCurrency={config.currency}
                  dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
                  marketplaceName={config.marketplaceName}
                  showListingImage={showListingImage}
                  hideMobileCTA
                  hideAuthor
                  reviewRating={averageRating}
                  reviewCount={reviewCount}
                />
              </div>
            </>
          )}
          </div>{/* /orderCol */}
        </div>

        {/* Mobile-only sticky booking bar. Hidden on desktop and for the
            owner via the component's own logic. Tier-aware price stays
            in sync with the in-page TierSelector via selectedTierKey.
            Stream 3 PR-3: for custom-quote listings the CTA opens
            the MobileQuoteSheet bottom-sheet modal (the desktop
            QuoteRequestPanel inline in the right rail is hidden on
            mobile via CSS). */}
        <MobileBookingBar
          listing={currentListing}
          selectedTierKey={selectedTierKey}
          marketplaceCurrency={config.currency}
          isOwnListing={isOwnListing}
          onCustomQuoteClick={() => setQuoteSheetOpen(true)}
        />

        <MobileQuoteSheet
          isOpen={quoteSheetOpen}
          onClose={() => setQuoteSheetOpen(false)}
          onManageDisableScrolling={onManageDisableScrolling}
          listing={currentListing}
          authorDisplayName={authorDisplayName}
          isOwnListing={isOwnListing}
          inProgress={requestQuoteInProgress}
          error={requestQuoteError}
          onSubmitQuoteRequest={values => {
            // Close only when the request actually went through — on failure
            // the sheet stays open so the error is visible (audit P0-2: it
            // previously closed unconditionally, a silent failure). Pass the
            // panel's full payload through: eventDate feeds the cancellation
            // clock via protectedData.
            onSubmitQuoteRequest(values).then(sent => {
              if (sent) setQuoteSheetOpen(false);
            });
          }}
        />
      </LayoutSingleColumn>
    </Page>
  );
};

/**
 * The ListingPage component with carousel layout.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.params - The path params object
 * @param {string} props.params.id - The listing id
 * @param {string} props.params.slug - The listing slug
 * @param {LISTING_PAGE_DRAFT_VARIANT | LISTING_PAGE_PENDING_APPROVAL_VARIANT} props.params.variant - The listing variant
 * @param {Function} props.onManageDisableScrolling - The on manage disable scrolling function
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated
 * @param {Function} props.getListing - The get listing function
 * @param {Function} props.getOwnListing - The get own listing function
 * @param {Object} props.currentUser - The current user
 * @param {boolean} props.scrollingDisabled - Whether scrolling is disabled
 * @param {string} props.inquiryModalOpenForListingId - The inquiry modal open for the specific listing id
 * @param {propTypes.error} props.showListingError - The show listing error
 * @param {Function} props.callSetInitialValues - The call setInitialValues function, which is given to this function as a parameter
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.fetchReviewsError - The fetch reviews error
 * @param {Object<string, Object>} props.monthlyTimeSlots - The monthly time slots. E.g. { '2019-11': { timeSlots: [], fetchTimeSlotsInProgress: false, fetchTimeSlotsError: null } }
 * @param {Object<string, Object>} props.timeSlotsForDate - The time slots for date. E.g. { '2019-11-01': { timeSlots: [], fetchedAt: 1572566400000, fetchTimeSlotsError: null, fetchTimeSlotsInProgress: false } }
 * @param {boolean} props.sendInquiryInProgress - Whether the send inquiry is in progress
 * @param {propTypes.error} props.sendInquiryError - The send inquiry error
 * @param {Function} props.onSendInquiry - The on send inquiry function
 * @param {Function} props.onInitializeCardPaymentData - The on initialize card payment data function
 * @param {Function} props.onFetchTimeSlots - The on fetch time slots function
 * @param {Function} props.onFetchTransactionLineItems - The on fetch transaction line items function
 * @param {Array<propTypes.transactionLineItem>} props.lineItems - The line items
 * @param {boolean} props.fetchLineItemsInProgress - Whether the fetch line items is in progress
 * @param {propTypes.error} props.fetchLineItemsError - The fetch line items error
 * @returns {JSX.Element} listing page component
 */
const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant != null;
  const currentUser = props.currentUser;
  if (isForbiddenError(showListingError) && !isVariant && !currentUser) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(showListingError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (hasNoViewingRights && isForbiddenError(showListingError)) ||
    isErrorNoViewingPermission(showListingError)
  ) {
    // If the user has no viewing rights, fetching anything but their own listings
    // will return a 403 error. If that happens, redirect to NoAccessPage.
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      showOwnListingsOnly={hasNoViewingRights}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    timeSlotsForDate,
    sendInquiryInProgress,
    sendInquiryError,
    requestQuoteInProgress,
    requestQuoteError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
  } = state.ListingPage;
  const { currentUser } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots, // for OrderPanel
    timeSlotsForDate, // for OrderPanel
    lineItems, // for OrderPanel
    fetchLineItemsInProgress, // for OrderPanel
    fetchLineItemsError, // for OrderPanel
    sendInquiryInProgress,
    sendInquiryError,
    requestQuoteInProgress,
    requestQuoteError,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)), // for OrderPanel
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onRequestQuote: (listing, message, protectedData) =>
    dispatch(requestQuote(listing, message, protectedData)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone, options) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone, options)), // for OrderPanel
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedListingPage);

export default ListingPage;
